// Auto-triage proposals — the shared read model for `teacherTasks.proposedTriage`.
// ---------------------------------------------------------------------------
// `triageWorkCapture` (Functions, onCreate) classifies a raw capture and writes a
// validated `proposedTriage` payload onto the same doc WITHOUT setting
// `triaged:true`. The human gate is structural: nothing commits until someone
// approves. This module is the one place that knows how to read that payload and
// say what approving it would actually DO to the task.
//
// Slice 1 (this) renders the diff. Slice 2's commit path consumes the SAME rows,
// so the surface and the write can never disagree about what "approve" means.
//
// THE PRIORITY CARVE-OUT (locked with Anthony 2026-07-25, from live measurement)
// Across the 37 live `proposed` payloads, every field is near-pure blank-filling
// — area 36 fills / 1 conflict, effort 24 / 0, dueDate 3 / 0, assignee 5 / 0 —
// except priority, which is filled on all 37 and conflicts on 19. That asymmetry
// is the whole design: priority is the only field where the model routinely
// overrides a value that is already there.
//
// It is softer than 19 sounds. `TaskService.createTask` defaults priority to
// 'medium' (TaskService.js:187) and its own comment at :198 says a medium default
// does NOT count as someone having sorted the task — so 18 of those 19 overwrite
// a default, which is exactly the job. Exactly one overrides a priority a human
// actually set. Hence: approve applies priority when the current value is the
// untouched default, and KEEPS the existing value when someone chose it.
// Everything else is all-or-nothing.

import { areaMeta, PEOPLE_BY_KEY } from './taskModel';

export const PRIORITY_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };
export const EFFORT_LABEL = { small: 'Small', medium: 'Medium', large: 'Large' };

// Verdicts a single field can carry. `kept` is the carve-out: the model proposed
// something, and approve will deliberately NOT apply it.
export const FILL = 'fill';
export const AGREE = 'agree';
export const OVERWRITE = 'overwrite';
export const KEPT = 'kept';

export function hasProposal(task) {
	return Boolean(task && task.proposedTriage);
}

export function isProposed(task) {
	return task?.proposedTriage?.status === 'proposed';
}

export function isNeedsInfo(task) {
	return task?.proposedTriage?.status === 'needsInfo';
}

// Already committed. approveTriage flips status rather than deleting the payload,
// so the record of what the classifier said and who accepted it survives — which
// means every "is there something to act on here?" check must exclude it, or an
// approved task keeps advertising a proposal forever.
export function isApproved(task) {
	return task?.proposedTriage?.status === 'approved';
}

// Is there an unresolved proposal a human still has to deal with? This — not
// hasProposal — is the queue/badge question.
export function isActionable(task) {
	return hasProposal(task) && !isApproved(task);
}

// Why approve is unavailable, or null if it is available. MUST stay in step with
// approvalBlockReason() in functions/triageApply.js — the callable is the real
// gate, this only decides whether the button is offered, and a disagreement
// means a button that errors on tap. triageApprove.parity.test.js asserts they
// return the same answer for the same task.
//
// `already-triaged` is the case Anthony chose to block (2026-07-25): a task
// sorted by hand or by the triage skill after the proposal was written would
// otherwise have a human decision overwritten by a stale suggestion. Matches
// retriageTask, which already refuses triaged tasks.
export function approvalBlockReason(task) {
	const t = task || {};
	const p = t.proposedTriage;
	if (!p) return 'no-proposal';
	if (t.space && t.space !== 'work') return 'not-work-space';
	if (p.status === 'approved') return 'already-approved'; // before triaged: approve sets both
	if (t.triaged === true) return 'already-triaged';
	if (p.status !== 'proposed') return 'not-proposed';
	return null;
}

export function canApprove(task) {
	return approvalBlockReason(task) === null;
}

// Did a human actually choose this priority? Absent or 'medium' = the create
// default, i.e. nobody sorted it. See the header note.
export function priorityWasChosen(task) {
	return Boolean(task?.priority) && task.priority !== 'medium';
}

const EMPTY = '—';

function areaLabel(area) {
	return area ? areaMeta(area).label : EMPTY;
}

function personLabel(key) {
	if (!key) return EMPTY;
	return PEOPLE_BY_KEY[key]?.name || key;
}

// task.deadline is already coerced to Date|null by TaskService; the proposal
// carries a plain 'YYYY-MM-DD' string. Compare on the date string alone — the
// proposal has no time component to be wrong about.
function deadlineKey(deadline) {
	if (!deadline) return null;
	const d = deadline instanceof Date ? deadline : new Date(deadline);
	if (Number.isNaN(d.getTime())) return null;
	const pad = (n) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDateKey(key) {
	if (!key) return EMPTY;
	const [y, m, d] = key.split('-').map(Number);
	if (!y || !m || !d) return key;
	return new Date(y, m - 1, d).toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
	});
}

function row(field, label, from, to, verdict) {
	return { field, label, from, to, verdict };
}

// The per-field diff of "what approving this proposal would do".
// Fields the model left null are omitted entirely — a proposal that says nothing
// about effort should not render a row claiming it does.
export function triageDiff(task) {
	if (!hasProposal(task)) return [];
	const p = task.proposedTriage;
	const rows = [];

	if (p.area) {
		const current = task.area || null;
		rows.push(
			row(
				'area',
				'Area',
				areaLabel(current),
				areaLabel(p.area),
				!current ? FILL : current === p.area ? AGREE : OVERWRITE
			)
		);
	}

	if (p.priority) {
		const current = task.priority || null;
		const chosen = priorityWasChosen(task);
		const same = current === p.priority;
		// Carve-out: a human-chosen priority is never overwritten by approve.
		const verdict = same ? AGREE : chosen ? KEPT : current ? OVERWRITE : FILL;
		rows.push(
			row(
				'priority',
				'Priority',
				current ? PRIORITY_LABEL[current] || current : EMPTY,
				PRIORITY_LABEL[p.priority] || p.priority,
				verdict
			)
		);
	}

	if (p.effort) {
		const current = task.effort || null;
		rows.push(
			row(
				'effort',
				'Effort',
				current ? EFFORT_LABEL[current] || current : EMPTY,
				EFFORT_LABEL[p.effort] || p.effort,
				!current ? FILL : current === p.effort ? AGREE : OVERWRITE
			)
		);
	}

	if (p.dueDate) {
		const current = deadlineKey(task.deadline);
		rows.push(
			row(
				'dueDate',
				'Due',
				formatDateKey(current),
				formatDateKey(p.dueDate),
				!current ? FILL : current === p.dueDate ? AGREE : OVERWRITE
			)
		);
	}

	if (p.assignee) {
		const uids = Array.isArray(task.assigneeUids) ? task.assigneeUids : [];
		const proposedUid = PEOPLE_BY_KEY[p.assignee]?.uid || null;
		const current = uids.length
			? uids.map((u) => personLabel(
				Object.keys(PEOPLE_BY_KEY).find((k) => PEOPLE_BY_KEY[k].uid === u)
			)).join(', ')
			: null;
		rows.push(
			row(
				'assignee',
				'Assignee',
				current || EMPTY,
				personLabel(p.assignee),
				!uids.length
					? FILL
					: proposedUid && uids.includes(proposedUid)
						? AGREE
						: OVERWRITE
			)
		);
	}

	return rows;
}

// Proposed sub-tasks are proposals, not documents — nothing exists until approve.
export function proposedSubtasks(task) {
	const subs = task?.proposedTriage?.subtasks;
	return Array.isArray(subs) ? subs.filter((s) => s && s.title) : [];
}

// One-line summary for the badge/queue: how many fields approve would change.
// AGREE and KEPT are deliberately not counted — neither changes the task.
export function changeCount(task) {
	return triageDiff(task).filter(
		(r) => r.verdict === FILL || r.verdict === OVERWRITE
	).length;
}
